<template>
  <div class="intro-page">
    <div class="intro-container">
      <!-- 顶部标题和语言切换 -->
      <div class="top-header">
        <div class="logo-title">
          <img src="../img/logo/png/Aii01.png" alt="Aii Logo" class="logo-img" />
          <div class="title-content">
            <h1>{{ $t('manual.productName') }}</h1>
            <p class="subtitle">{{ $t('intro.title') }}</p>
          </div>
        </div>
        <div class="language-switcher">
          <button @click="changeLanguage('zh')" :class="{ active: locale === 'zh' }">中文</button>
          <button @click="changeLanguage('en')" :class="{ active: locale === 'en' }">English</button>
          <button @click="changeLanguage('es')" :class="{ active: locale === 'es' }">Español</button>
        </div>
      </div>
      <!-- 选项卡导航 -->
      <div class="tabs-container">
        <div class="tabs-nav">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="{ active: activeTab === tab.id }"
          class="tab-btn"
        >
            <span class="tab-icon">{{ tab.icon }}</span>
            <span class="tab-text">{{ $t(tab.title) }}</span>
          </button>
        </div>
        <!-- 选项卡内容 -->
        <div class="tabs-content">
          <!-- 步骤1：开启能量链接 -->
          <div v-if="activeTab === 'step1'" class="tab-pane">
            <div class="tab-header">
              <h2>{{ $t('manual.section1Title') }}</h2>
              <p class="tab-desc">{{ $t('manual.section1Desc') }}</p>
            </div>
            
            <div class="steps-grid">
              <div class="step-card" v-for="(step, index) in 7" :key="index">
                <div class="step-header">
                  <div class="step-number">{{ index + 1 }}</div>
                  <h3>{{ $t(`manual.section1Steps.step${index + 1}`) }}</h3>
                </div>
              </div>
            </div>
            
            <div class="conclusion-card">
              <div class="conclusion-icon">✅</div>
              <p>{{ $t('manual.section1Conclusion') }}</p>
            </div>
          </div>
          <!-- 步骤2：日常使用 -->
          <div v-if="activeTab === 'step2'" class="tab-pane">
            <div class="tab-header">
              <h2>{{ $t('manual.section2Title') }}</h2>
            </div>
            
            <div class="usage-cards">
              <div class="usage-card" v-for="(point, index) in 3" :key="index">
                <div class="usage-icon">📌</div>
                <div class="usage-content">
                  <p>{{ $t(`manual.section2Points.point${index + 1}`) }}</p>
                </div>
              </div>
            </div>
          </div>
          <!-- 步骤3：常见问题 -->
          <div v-if="activeTab === 'step3'" class="tab-pane">
            <div class="tab-header">
              <h2>{{ $t('manual.section3Title') }}</h2>
            </div>
            
            <div class="faq-accordion">
              <div class="faq-item" v-for="(faq, index) in faqs" :key="index">
                <div class="faq-question" @click="toggleFaq(index)">
                  <h3>{{ $t(faq.q) }}</h3>
                  <span class="faq-toggle">{{ expandedFaqs[index] ? '−' : '+' }}</span>
                </div>
                <div class="faq-answer" v-if="expandedFaqs[index]">
                  <p>{{ $t(faq.a) }}</p>
                  <ul v-if="faq.points" class="faq-points">
                    <li v-for="(point, pointIndex) in faq.points" :key="pointIndex">
                      <span class="point-number">{{ pointIndex + 1 }}.</span> {{ $t(point) }}
                    </li>
                  </ul>
                  <p v-if="faq.conclusion" class="faq-conclusion">{{ $t(faq.conclusion) }}</p>
                </div>
              </div>
            </div>
          </div>
          <!-- 联系信息 -->
          <div v-if="activeTab === 'contact'" class="tab-pane">
            <div class="tab-header">
              <h2>{{ $t('manual.contact.title') }}</h2>
            </div>
            
            <div class="contact-cards">
              <div class="contact-card">
                <div class="contact-icon">💳</div>
                <div class="contact-details">
                  <h3>{{ $t('manual.contact.rechargeService') }}</h3>
                </div>
              </div>
              
              <div class="contact-card">
                <div class="contact-icon">📧</div>
                <div class="contact-details">
                  <h3>{{ $t('manual.contact.emailbefore') }}&#64;{{ $t('manual.contact.emailafter') }}</h3>
                </div>
              </div>
              <div class="contact-card" @click="window.open('https://aiinovavida.com/', '_blank')" style="cursor: pointer;">
                  <div class="contact-icon">🌐</div>
                  <div class="contact-details">
                    <h3>{{ $t('manual.contact.website') }}</h3>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 底部信息和操作按钮 -->
      <div class="bottom-section">
        <div class="footer-logo">
          <img src="../img/logo/png/Aii01.png" alt="Aii Logo" class="footer-logo-img" />
          <div class="footer-text">
            <p>{{ $t('manual.footer') }}</p>
          </div>
        </div>
        
        <div class="hint-text">{{ $t('intro.hint') }}</div>
        <div class="action-buttons">
          <button class="next-btn" @click="handleNext">{{ $t('intro.next') }}</button>
          <button class="exit-btn" @click="handleExit">{{ $t('intro.exit') }}</button>
        </div>
      </div>
    </div>

    <!-- 弹窗提示：滑倒最低端测八字 -->
    <div class="modal-overlay" v-if="showModal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-icon">🔮</div>
        <div class="modal-text">{{ $t('intro.modalHint') }}</div>
        <button class="modal-close-btn" @click="closeModal">{{ $t('intro.next') }}</button>
      </div>
    </div>
  </div>
</template>
<script setup>
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ref, onMounted } from 'vue'
import './IntroPage.css'
const router = useRouter()
const { locale } = useI18n()

// 弹窗控制
const showModal = ref(false)
const closeModal = () => {
  showModal.value = false
}
// 选项卡数据
const tabs = ref([
  { id: 'step1', title: 'manual.section1Title', icon: '🔮' },
  { id: 'step2', title: 'manual.section2Title', icon: '📋' },
  { id: 'step3', title: 'manual.section3Title', icon: '❓' },
  { id: 'contact', title: 'manual.contact.title', icon: '📞' }
])
const activeTab = ref('step1')
// FAQ数据
const faqs = ref([
  { q: 'manual.faq.q1', a: 'manual.faq.a1' },
  { q: 'manual.faq.q2', a: 'manual.faq.a2' },
  { q: 'manual.faq.q3', a: 'manual.faq.a3' },
  { 
    q: 'manual.faq.q4', 
    a: 'manual.faq.a4',
    points: [
      'manual.faq.a4Points.point1',
      'manual.faq.a4Points.point2',
      'manual.faq.a4Points.point3',
      'manual.faq.a4Points.point4'
    ],
    conclusion: 'manual.faq.a4Conclusion'
  }
])
const expandedFaqs = ref([])
const toggleFaq = (index) => {
  expandedFaqs.value[index] = !expandedFaqs.value[index]
}
// 检测系统语言并设置默认语言（与main.js保持一致）
const detectSystemLanguage = () => {
  const systemLang = navigator.language || navigator.userLanguage || 'en'
  
  // 检查localStorage中是否有用户选择的语言
  const savedLocale = localStorage.getItem('userLocale')
  if (savedLocale && ['zh', 'en', 'es'].includes(savedLocale)) {
    locale.value = savedLocale
    return
  }
  
  // 根据系统语言设置默认语言
  if (systemLang.startsWith('zh')) {
    locale.value = 'zh'
  } else if (systemLang.startsWith('en')) {
    locale.value = 'en'
  } else if (systemLang.startsWith('es')) {
    locale.value = 'es'
  } else {
    // 其他语言默认显示英语
    locale.value = 'en'
  }
  
  // 保存到localStorage
  localStorage.setItem('userLocale', locale.value)
}
const changeLanguage = (lang) => {
  locale.value = lang
  // 保存语言选择到localStorage
  localStorage.setItem('userLocale', lang)
}
const handleExit = () => {
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push('/')
  }
}
const handleNext = () => {
  router.push('/form')
}
// 组件挂载时初始化
onMounted(() => {
  detectSystemLanguage()
  expandedFaqs.value = new Array(faqs.value.length).fill(false)
  // 延迟显示弹窗，让页面先渲染完成
  setTimeout(() => {
    showModal.value = true
  }, 500)
})
</script>